import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import {
  clonePageContext,
  createFallbackPageContext,
  createPageContextKey,
  type PageContext,
  type PageContextTransition,
} from '@/hooks/folio-page-context-contract';
import {
  FolioSessionCommandsContext,
  FolioSessionContext,
  type FolioSessionContextValue,
  FolioSessionStateContext,
  type FolioSessionCommandsValue,
  type FolioSessionStateValue,
  type PausedFolioConversation,
} from '@/hooks/folio-session-context';
import { resolveRoutePage } from '@/folio/route-page';
import { ProjectCtx } from '@/hooks/project-context';
import { TenantCtx } from '@/hooks/tenant-context';
import { projectExecutionLogPath } from '@/lib/routes';
import {
  useFolioStream,
  type ActionCard,
  type FolioActionUiHandoff,
  type GuideStep,
} from '@/hooks/use-folio-stream';
import {
  hasFolioProjectScopeMismatch,
  resolveFolioProjectId,
} from '@/components/folio/folio-scope';

function buildPendingContextTransition(
  previousPageContext: PageContext | null,
  currentPageContext: PageContext,
): PageContextTransition | null {
  if (previousPageContext === null) {
    return null;
  }

  if (createPageContextKey(previousPageContext) === createPageContextKey(currentPageContext)) {
    return null;
  }

  return {
    kind: 'navigation',
    from_page: previousPageContext.page,
    from_entities: { ...previousPageContext.entities },
  };
}

function withContextTransition(
  pageContext: PageContext,
  pendingContextTransition: PageContextTransition | null,
): PageContext {
  if (pendingContextTransition === null) {
    return clonePageContext(pageContext);
  }

  return {
    ...clonePageContext(pageContext),
    context_transition: {
      kind: pendingContextTransition.kind,
      from_page: pendingContextTransition.from_page,
      from_entities: { ...pendingContextTransition.from_entities },
    },
  };
}

function resolveActionDestination(
  handoff: FolioActionUiHandoff | undefined,
  tenantSlug: string | null,
): string | null {
  if (handoff === undefined || tenantSlug === null) {
    return null;
  }

  switch (handoff.intent) {
    case 'open_upload':
      return handoff.project_id === undefined
        ? null
        : `/${tenantSlug}/projects/${handoff.project_id}/upload-documents`;
    case 'open_template_editor':
      return handoff.template_id === undefined
        ? null
        : `/${tenantSlug}/templates/${handoff.template_id}/edit`;
    case 'open_run':
      return handoff.project_id === undefined || handoff.run_id === undefined
        ? null
        : projectExecutionLogPath(tenantSlug, handoff.project_id, {
            runId: handoff.run_id,
          });
    case 'open_evaluation':
      return handoff.project_id === undefined || handoff.evaluation_run_id === undefined
        ? null
        : `/${tenantSlug}/projects/${handoff.project_id}/evaluations/${handoff.evaluation_run_id}`;
    case 'open_route':
      return handoff.deep_link !== undefined && isSameOriginPath(handoff.deep_link)
        ? handoff.deep_link
        : null;
  }
}

interface FolioNavigationState {
  readonly folio_target_highlight: {
    readonly target_test_id: string;
    readonly page_id?: string;
    readonly page_name?: string;
  };
}

function isSameOriginPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//');
}

function resolveActionNavigationState(
  handoff: FolioActionUiHandoff | undefined,
): FolioNavigationState | null {
  if (
    handoff?.intent !== 'open_route' ||
    handoff.target_test_id === undefined ||
    handoff.target_test_id.length === 0
  ) {
    return null;
  }
  return {
    folio_target_highlight: {
      target_test_id: handoff.target_test_id,
      ...(handoff.page_id === undefined ? {} : { page_id: handoff.page_id }),
      ...(handoff.page_name === undefined ? {} : { page_name: handoff.page_name }),
    },
  };
}

const HANDOFF_LABELS: Record<FolioActionUiHandoff['intent'], string> = {
  open_upload: 'Upload Documents',
  open_template_editor: 'Template Editor',
  open_run: 'Run Details',
  open_evaluation: 'Evaluation Results',
  open_route: 'destination',
};

const PAUSED_PAGE_LABELS: Record<PageContext['page'], string> = {
  'unregistered-route': 'previous',
  'quick-answer': 'quick answer',
  dashboard: 'dashboard',
  admin: 'admin',
  members: 'members',
  'project-detail': 'project',
  templates: 'templates',
  'template-detail': 'template',
  'template-editor': 'template editor',
  'run-detail': 'run',
  'run-results': 'run results',
  'analysis-workspace': 'analysis',
  analyses: 'analyses',
  documents: 'documents',
  evaluations: 'evaluations',
  'evaluation-detail': 'evaluation',
  'evaluation-compare': 'evaluation compare',
  'ground-truth': 'ground truth',
  'ground-truth-annotate': 'ground truth review',
  'document-detail': 'document',
  'document-upload': 'upload',
  'control-plane': 'control plane',
};

function describePausedConversation(
  priorPageContext: PageContext | null,
  priorProjectId: string | null,
): string {
  if (priorPageContext !== null) {
    const label = PAUSED_PAGE_LABELS[priorPageContext.page];
    return `Previous ${label} thread`;
  }
  return priorProjectId === null ? 'Previous tenant thread' : 'Previous project thread';
}

function FolioSessionProviderInner({
  children,
  fallbackPageContext,
  tenantSlug,
}: {
  readonly children: ReactNode;
  readonly fallbackPageContext: PageContext;
  readonly tenantSlug: string | null;
}) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationProjectId, setConversationProjectId] = useState<string | null>(null);
  const [conversationPageContext, setConversationPageContext] = useState<PageContext | null>(null);
  const [registeredPageContext, setRegisteredPageContext] = useState<PageContext | null>(null);
  const [pausedConversation, setPausedConversation] = useState<PausedFolioConversation | null>(null);
  const navigate = useNavigate();
  const {
    messages,
    isStreaming,
    isBusy,
    currentMode,
    error,
    sendMessage,
    executeActionCard,
    stopStream,
    retryLastMessage,
    loadConversation,
    clearMessages,
  } = useFolioStream();
  const isBusyRef = useRef(isBusy);

  useEffect(() => {
    isBusyRef.current = isBusy;
  }, [isBusy]);

  // A page's own registration replaces the route's registered page and its
  // capabilities, which describe the page rather than what this user may do.
  const effectivePageContext = useMemo<PageContext>(() => {
    if (registeredPageContext === null) {
      return fallbackPageContext;
    }

    return {
      ...clonePageContext(registeredPageContext),
      entities: {
        ...fallbackPageContext.entities,
        ...registeredPageContext.entities,
      },
    };
  }, [fallbackPageContext, registeredPageContext]);
  const pendingContextTransition = useMemo(() => {
    if (conversationId === null || messages.length === 0) {
      return null;
    }

    return buildPendingContextTransition(conversationPageContext, effectivePageContext);
  }, [conversationId, conversationPageContext, effectivePageContext, messages.length]);

  const clearConversation = useCallback(() => {
    clearMessages();
    setConversationId(null);
    setConversationProjectId(null);
    setConversationPageContext(null);
    setPausedConversation(null);
  }, [clearMessages]);

  const handleSendMessage = useCallback(
    (text: string, pageContextOverride?: PageContext) => {
      const trimmedText = text.trim();
      if (trimmedText.length === 0 || isBusyRef.current) {
        return;
      }

      const nextPageContext = clonePageContext(pageContextOverride ?? effectivePageContext);
      const nextConversationProjectId = resolveFolioProjectId(nextPageContext.entities);
      const shouldStartFreshConversation =
        conversationId !== null &&
        hasFolioProjectScopeMismatch(conversationProjectId, nextPageContext);
      const nextConversationId =
        shouldStartFreshConversation || conversationId === null
          ? crypto.randomUUID()
          : conversationId;
      const outgoingPageContext = shouldStartFreshConversation
        ? clonePageContext(nextPageContext)
        : withContextTransition(nextPageContext, pendingContextTransition);
      if (shouldStartFreshConversation) {
        setPausedConversation({
          id: conversationId,
          projectId: conversationProjectId,
          label: describePausedConversation(conversationPageContext, conversationProjectId),
        });
        clearMessages();
      }
      if (conversationId === null || shouldStartFreshConversation) {
        setConversationId(nextConversationId);
        setConversationProjectId(nextConversationProjectId);
      }
      setConversationPageContext(nextPageContext);

      void sendMessage(trimmedText, nextConversationId, outgoingPageContext);
    },
    [
      clearMessages,
      conversationId,
      conversationPageContext,
      conversationProjectId,
      effectivePageContext,
      pendingContextTransition,
      sendMessage,
    ],
  );

  const handleRetryLastMessage = useCallback(() => {
    void retryLastMessage();
  }, [retryLastMessage]);

  const handleLoadConversation = useCallback(
    async (nextConversationId: string, nextConversationProjectId?: string | null) => {
      if (isBusyRef.current) {
        return;
      }
      const restoredConversation = await loadConversation(nextConversationId);
      setConversationId(nextConversationId);
      setConversationProjectId(
        restoredConversation.conversationProjectId === undefined
          ? (nextConversationProjectId ?? null)
          : restoredConversation.conversationProjectId,
      );
      setConversationPageContext(
        restoredConversation.lastPageContext === null
          ? clonePageContext(effectivePageContext)
          : clonePageContext(restoredConversation.lastPageContext),
      );
      setPausedConversation(null);
    },
    [effectivePageContext, loadConversation],
  );

  const resumePausedConversation = useCallback(async () => {
    if (pausedConversation === null) {
      return;
    }
    try {
      await handleLoadConversation(pausedConversation.id, pausedConversation.projectId);
    } catch {
      // Surface left untouched so the paused chip remains available for retry.
    }
  }, [handleLoadConversation, pausedConversation]);

  const dismissPausedConversation = useCallback(() => {
    setPausedConversation(null);
  }, []);

  const handleConfirmActionCard = useCallback(
    async (action: ActionCard, guideStep?: GuideStep) => {
      if (conversationId === null || isBusyRef.current) {
        return;
      }
      const result = await executeActionCard(action, conversationId, effectivePageContext);
      if (result.conversation_project_id !== undefined) {
        setConversationProjectId(result.conversation_project_id ?? null);
      }
      const destination = resolveActionDestination(result.ui_handoff, tenantSlug);
      if (destination !== null) {
        const label =
          result.ui_handoff !== undefined ? HANDOFF_LABELS[result.ui_handoff.intent] : null;
        if (label !== null) {
          toast('✦ Sean', {
            description: `Navigating to ${label}`,
            duration: 3000,
          });
        }
        const navigationState = resolveActionNavigationState(result.ui_handoff);
        void navigate(
          destination,
          navigationState === null ? undefined : { state: navigationState },
        );
      }

      // After a successful guide-mode action, nudge the model to advance the
      // stepper by sending a lightweight follow-up message.
      if (guideStep !== undefined && result.status === 'completed') {
        handleSendMessage(
          `Step ${String(guideStep.step)} of ${String(guideStep.total)} completed. Continue to the next step.`,
        );
      }
    },
    [conversationId, effectivePageContext, executeActionCard, handleSendMessage, navigate, tenantSlug],
  );

  const stateValue = useMemo<FolioSessionStateValue>(
    () => ({
      messages,
      isStreaming,
      isBusy,
      currentMode,
      error,
      conversationProjectId,
      pendingContextTransition,
      pausedConversation,
    }),
    [
      conversationProjectId,
      currentMode,
      error,
      isBusy,
      isStreaming,
      messages,
      pausedConversation,
      pendingContextTransition,
    ],
  );

  const commandsValue = useMemo<FolioSessionCommandsValue>(
    () => ({
      pageContext: effectivePageContext,
      sendMessage: handleSendMessage,
      stopStreaming: stopStream,
      confirmActionCard: handleConfirmActionCard,
      retryLastMessage: handleRetryLastMessage,
      setPageContext: setRegisteredPageContext,
      conversationId,
      loadConversation: handleLoadConversation,
      clearConversation,
      resumePausedConversation,
      dismissPausedConversation,
    }),
    [
      clearConversation,
      conversationId,
      dismissPausedConversation,
      effectivePageContext,
      handleConfirmActionCard,
      handleLoadConversation,
      handleRetryLastMessage,
      handleSendMessage,
      resumePausedConversation,
      stopStream,
    ],
  );

  const value = useMemo<FolioSessionContextValue>(
    () => ({ ...stateValue, ...commandsValue }),
    [commandsValue, stateValue],
  );

  return (
    <FolioSessionCommandsContext value={commandsValue}>
      <FolioSessionStateContext value={stateValue}>
        <FolioSessionContext value={value}>{children}</FolioSessionContext>
      </FolioSessionStateContext>
    </FolioSessionCommandsContext>
  );
}

export function FolioSessionProvider({ children }: { readonly children: ReactNode }) {
  const location = useLocation();
  const routeParams = useParams();
  const tenantContext = useContext(TenantCtx);
  const projectContext = useContext(ProjectCtx);

  const fallbackPageContext = useMemo(
    () =>
      createFallbackPageContext(
        location.pathname,
        routeParams,
        tenantContext?.tenantId ?? null,
        tenantContext?.slug ?? routeParams['tenantSlug'] ?? null,
        projectContext?.project.project_id ?? null,
        resolveRoutePage(location.pathname, location.search),
      ),
    [location.pathname, location.search, projectContext, routeParams, tenantContext],
  );

  const routingContextKey = [
    tenantContext?.tenantId ?? routeParams['tenantSlug'] ?? '',
  ].join(':');
  const tenantSlug = tenantContext?.slug ?? routeParams['tenantSlug'] ?? null;

  return (
    <FolioSessionProviderInner
      key={routingContextKey}
      fallbackPageContext={fallbackPageContext}
      tenantSlug={tenantSlug}
    >
      {children}
    </FolioSessionProviderInner>
  );
}
